import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';
import { HttpService } from '@mona/api';
import { Encounter } from '@mona/models';
import { AuditEvent, Bundle } from '../../../../shared/src/lib/types/r4b.type';

const baseApi = '/pdms/fhir';

/**
 * API abstraction layer for the Audit Log API
 */
@Injectable({
    providedIn: 'root',
})
export class AuditLogApi {
    /**
     * Constructor
     *
     * @param http HttpService
     */
    constructor(private http: HttpService) {}

    /**
     * Get audit logs for an encounter
     *
     * @param encounter
     * @param backendPage
     * @param backendPageSize
     * @param paginationId
     */
    getAuditLogs(
        encounter: Encounter,
        backendPage: number,
        backendPageSize = 50,
        paginationId: string = null,
    ): Observable<{ auditLogs: AuditEvent[] }> {
        const initialLoad = backendPage === 0;
        const date = [`ge${encounter.startDate.toISOString()}`];
        if (encounter.endDate) {
            date.push(`le${encounter.endDate.toISOString()}`);
        }

        let params = {
            'entity-name': `Patient/${encounter.patientId}`,
            _sort: '-date',
            date,
        } as any;

        if (!initialLoad) {
            params = {
                _getpages: paginationId,
                _getpagesoffset: backendPageSize * backendPage,
                _count: backendPageSize,
                _pretty: true,
                _bundletype: 'searchset',
            };
        }

        return this.http.get<Bundle<AuditEvent>>(baseApi + (initialLoad ? '/AuditEvent' : ''), { params }).pipe(
            map(bundle => {
                if (bundle.resourceType !== 'Bundle') {
                    throw new Error('Invalid FHIR Bundle');
                }
                const auditLogs = bundle.entry
                    .map(entry => entry.resource as AuditEvent)
                    .filter(resource => resource.resourceType === 'AuditEvent');

                return {
                    auditLogs,
                    totalCount: bundle.total,
                    paginationId: bundle.id,
                    backendPageSize: initialLoad ? auditLogs?.length || 0 : backendPageSize,
                };
            }),
        );
    }
}
